import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Reveal-on-click pricing walk: base → markup → discounts → contract → cost → profit/margin.
export default function PricingBreakdown({ summary }) {
  const [open, setOpen] = useState(false);
  const base = Number(summary.subtotal || 0);
  const marked = Number(summary.marked_up_total || 0);
  const markupPct = (Number(summary.markup || 1) - 1) * 100;
  const grand = Number(summary.grand_total || 0);
  const cost = Number(summary.total_cost || 0);
  const rows = [
    ['Quote total before markup & discounts', base, 'pb-base'],
    [`Markup ${markupPct.toFixed(1)}%`, marked - base, 'pb-markup'],
    ...(summary.discounts || []).map(d => [`${d.label} ${d.percent}%`, -marked * d.percent / 100, `pb-discount-${d.key}`]),
    ...(Number(summary.extra_discount_amount) > 0 ? [['Extra $ discount', -Number(summary.extra_discount_amount), 'pb-extra']] : []),
    ['Contract amount (excl. GST)', grand, 'pb-contract', true],
    ['Our cost — materials buy-in', Number(summary.total_buy_cost || 0), 'pb-buy'],
    ['Our cost — contractor', Number(summary.total_contractor || 0), 'pb-contractor'],
    ['Profit', grand - cost, 'pb-profit', true],
  ];
  return (
    <div className="mt-4 border border-slate-200 rounded-md" data-testid="pricing-breakdown">
      <button type="button" data-testid="pricing-breakdown-toggle" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-50 transition-colors">
        {open ? 'Hide' : 'Show'} pricing breakdown {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1 text-sm">
          {rows.map(([label, v, id, strong]) => (
            <div key={id} data-testid={id} className={`flex justify-between ${strong ? 'font-semibold border-t border-slate-200 pt-1' : 'text-slate-600'}`}>
              <span>{label}</span><span className="font-mono-num">{money(v)}</span>
            </div>
          ))}
          <div data-testid="pb-margin" className="flex justify-between font-semibold text-emerald-700">
            <span>Margin</span><span className="font-mono-num">{grand > 0 ? `${((grand - cost) / grand * 100).toFixed(1)}%` : '—'}</span>
          </div>
          {Number(summary.uncosted_count) > 0 && (
            <p data-testid="pb-uncosted-warning" className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-300 rounded px-2 py-1">
              {summary.uncosted_count} line{summary.uncosted_count === 1 ? ' has' : 's have'} no buy-in or contractor cost — profit and margin are overstated. Add costs on the rate cards.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
